import { state } from '../state';
// Chat channels replaced people/jobs/schedule/general views
import { renderAgreements } from '../channels/agreements';
import { renderApprovals } from '../channels/approvals';
import { renderDirectory } from '../channels/directory';
import { renderChat, cleanupChat } from '../channels/chat';
import { renderContractor } from '../channels/contractor';
import { renderRules } from '../channels/rules';
import { renderLogs } from '../channels/logs';
import { renderSettings } from '../channels/settings';
import { renderRoles } from '../channels/roles';
import { renderVoicePanel } from '../channels/voice';
import { renderDevsLaunch, cleanupDevsLaunch } from '../channels/devs-launch';
import { renderDevDashboard, cleanupDevDashboard } from '../channels/dev-dashboard';
import { renderWatcherFeed, cleanupWatcherFeed } from '../channels/watcher-feed';
import { renderEmailInbox, cleanupEmailInbox } from '../channels/email-inbox';
import { renderCcChat, cleanupCcChat } from '../channels/cc-chat';
import { renderSms, cleanupSms } from '../channels/sms';
import { renderCellStatus, cleanupCellStatus } from '../channels/cell-status';
import { renderCalls, cleanupCalls } from '../channels/calls';
import { renderNotes137, cleanupNotes137 } from '../channels/notes-137';
import { renderPeerChat, cleanupPeerChat } from '../channels/peer-chat';
import { renderDevelopment, cleanupDevelopment } from '../channels/development';
import { renderBoard, cleanupBoard } from '../channels/board';
import { renderABTesting } from '../channels/ab-testing';
import { renderSystemHealth, cleanupSystemHealth } from '../channels/system-health';
import { renderAiTasks, cleanupAiTasks } from '../channels/ai-tasks';
import { renderDocs, cleanupDocs } from '../channels/docs';
import { renderBlog, cleanupBlog } from '../channels/blog';
import { renderAiQuestions, cleanupAiQuestions } from '../channels/ai-questions';
import type { HomeCallbacks } from '../pages/home';
import { renderDmHome, cleanupDms } from '../pages/dms';
import { iconHash, iconUsers, iconBell, iconMessageSquare, iconList } from '../icons';
import { renderMemberList } from './member-list';
import { skeletonCard, skeletonStats } from '../components/skeleton';
import { openProfileCard } from '../components/profile-card';
import { showToast } from '../components/toast';

export function renderContentArea(container: HTMLElement, _homeCallbacks: HomeCallbacks): void {
  // Clean up ALL channel renderers BEFORE any DOM manipulation so they can
  // still read scroll positions, timers, etc. from the live DOM.
  cleanupChat();
  cleanupDevsLaunch();
  cleanupEmailInbox();
  cleanupCcChat();
  cleanupSms();
  cleanupCellStatus();
  cleanupNotes137();
  cleanupCalls();
  cleanupPeerChat();
  cleanupDevDashboard();
  cleanupWatcherFeed();
  cleanupDevelopment();
  cleanupBoard();
  cleanupSystemHealth();
  cleanupAiTasks();
  cleanupDocs();
  cleanupBlog();
  cleanupAiQuestions();
  cleanupDms();
  // Tear down any transient header popovers/panels from the prior channel —
  // they're parented to <body> or the old container and would otherwise leak.
  ['notif-popover', 'threads-panel', 'inbox-panel', 'ca-inbox-flyout', 'pins-panel', 'search-results']
    .forEach(id => document.getElementById(id)?.remove());

  if (!state.currentServer) {
    // Home view = DMs (like Discord). DMs are 1:1 — there is no server
    // member list, so clear and hide #member-list so the stale server
    // roster from the previous channel doesn't bleed into the DM view
    // alongside the conversation panel.
    clearServerMemberList();
    container.innerHTML = '';
    const body = document.createElement('div');
    body.className = 'ca-body ca-body-chat';
    body.style.height = '100%';
    container.appendChild(body);
    renderDmHome(body);
    return;
  }

  const ch = state.currentChannel;

  // Board view sentinel: "board:CategoryName" — renders the card/kanban
  // board for a parent category. No channel header needed.
  const isBoard = typeof ch === 'string' && ch.startsWith('board:');
  if (isBoard) {
    const boardCategory = ch.slice('board:'.length);
    container.innerHTML = '';
    const body = document.createElement('div');
    body.className = 'ca-body ca-body-chat';
    body.style.height = '100%';
    container.appendChild(body);
    void renderBoard(body, boardCategory);
    return;
  }

  // Voice channels use a `voice:<id>` sentinel for currentChannel.
  const isVoice = typeof ch === 'string' && ch.startsWith('voice:');
  const voiceId = isVoice ? ch.slice('voice:'.length) : '';
  const voiceCh = voiceId ? state.voiceChannels.find(v => v.id === voiceId) : null;
  const headerLabel = isVoice && voiceCh ? voiceCh.name : String(ch);
  const headerIcon = isVoice
    ? '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>'
    : iconHash();

  // Encryption badge — read from the loaded channel list. Lock icon when
  // the channel is not "standard"; click jumps to the Encryption tab.
  const channelRow = state.channels.find(c => c.name === String(ch) && (!c.server_id || c.server_id === state.currentServer));
  const encLevel = (channelRow?.encryption_level ?? 'standard') as 'standard' | 'encrypted' | 'e2ee';
  const lockBadge = encLevel === 'standard' ? '' : `
    <button class="ca-header-btn" id="ca-enc-badge" title="${encLevel === 'e2ee' ? 'End-to-end (encrypted at rest fallback)' : 'Encrypted at rest'} — click for settings" data-channel-id="${escHtml(channelRow?.id ?? '')}" data-channel-name="${escHtml(String(ch))}">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10z"/></svg>
    </button>`;

  container.innerHTML = `
    <div class="ca-header">
      <button class="ca-header-back ca-mobile-only" id="ca-header-back" aria-label="Back to channel list" type="button">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
      </button>
      <span class="ca-header-icon">${headerIcon}</span>
      <span class="ca-header-title">${escHtml(headerLabel)}</span>
      ${lockBadge}
      <span class="ca-topic-divider" aria-hidden="true">·</span>
      <span class="ca-header-topic ca-topic" id="ca-topic" title="Edit channel topic" style="cursor:pointer" data-channel-id="${escHtml(channelRow?.id ?? '')}" data-channel-name="${escHtml(String(ch))}"></span>
      <div class="ca-header-actions" id="ca-actions">
        <span class="ca-rt-status" id="ca-rt-status" role="button" tabindex="0" aria-label="Message delivery mode"></span>
        <button class="ca-header-btn" id="threads-toggle" title="Threads" aria-label="Threads">
          ${iconMessageSquare()}
        </button>
        <button class="ca-header-btn" id="pins-toggle" title="Pinned messages" aria-label="Pinned messages">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style="opacity:0.6"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>
          <span class="ca-pin-badge" id="ca-pin-badge" aria-hidden="true" style="display:none"></span>
        </button>
        <button class="ca-header-btn" id="notifications-toggle" title="Notification settings" aria-label="Notification settings">
          ${iconBell()}
        </button>
        <button class="ca-header-btn" id="members-toggle" title="Toggle member list" aria-label="Toggle member list">
          ${iconUsers()}
          <span class="ca-member-count" id="ca-member-count"></span>
        </button>
        <div class="ca-search-wrap" id="ca-search-wrap">
          <input type="text" class="ca-search-input" id="ca-search" placeholder="Search" />
        </div>
        <button class="ca-header-btn" id="inbox-toggle" title="Inbox" aria-label="Inbox">
          ${iconList()}
        </button>
      </div>
    </div>
  `;

  const body = document.createElement('div');
  body.className = 'ca-body';
  container.appendChild(body);

  // Render member list
  const memberPanel = document.getElementById('member-list');
  if (memberPanel) renderMemberList(memberPanel);

  // Member count in header
  const mCount = state.currentServer
    ? state.serverMembers.filter(m => m.server_id === state.currentServer).length
    : 0;
  const countEl = document.getElementById('ca-member-count');
  if (countEl && mCount > 0) countEl.textContent = String(mCount);

  // Populate the channel topic from the loaded ServerChannel row so the
  // "·" divider + clipped one-liner with hover-tooltip (Discord parity)
  // shows the topic right of the channel name. Empty when no topic set.
  const topicEl = document.getElementById('ca-topic');
  const dividerEl = document.querySelector<HTMLElement>('.ca-topic-divider');
  if (topicEl) {
    const topic = (channelRow?.topic ?? '').trim();
    if (topic) {
      topicEl.textContent = topic;
      topicEl.setAttribute('title', topic);
      if (dividerEl) dividerEl.style.display = '';
    } else {
      topicEl.textContent = '';
      topicEl.removeAttribute('title');
      if (dividerEl) dividerEl.style.display = 'none';
    }
  }

  // Pinned-messages count badge — Discord parity. Async fetch so the header
  // renders immediately and the badge fades in once the count comes back.
  if (state.currentServer && channelRow?.id) {
    const sid = state.currentServer;
    void import('../api').then(({ get: fetchGet }) => {
      void fetchGet<Array<{id:string}>>(`/api/messages/pinned?server_id=${encodeURIComponent(sid)}&channel=${encodeURIComponent(String(ch))}`)
        .then(pins => {
          const badge = document.getElementById('ca-pin-badge');
          if (!badge) return;
          if (pins.length > 0) {
            badge.textContent = String(pins.length > 99 ? '99+' : pins.length);
            badge.style.display = '';
          } else {
            badge.style.display = 'none';
          }
        })
        .catch(() => { /* endpoint may be off — leave hidden */ });
    });
  }

  // Mobile back button — open the channel list drawer. Reuses the same
  // `.channel-list.open` + `#sidebar-scrim.open` mechanism wired up in
  // app.ts initMobileSidebar so we stay consistent with swipe/hamburger.
  document.getElementById('ca-header-back')?.addEventListener('click', () => {
    document.getElementById('server-rail')?.classList.add('open');
    document.getElementById('channel-list')?.classList.add('open');
    document.getElementById('sidebar-scrim')?.classList.add('open');
  });

  document.getElementById('threads-toggle')?.addEventListener('click', async () => {
    const existing = document.getElementById('threads-panel');
    if (existing) { existing.remove(); return; }
    const sid = state.currentServer;
    if (!sid) return;
    const panel = document.createElement('div');
    panel.id = 'threads-panel';
    panel.className = 'side-panel';
    panel.innerHTML = `<div class="side-panel-header">Threads<button class="side-panel-close" aria-label="Close">&times;</button></div><div class="side-panel-body" id="threads-panel-body">Loading…</div>`;
    container.appendChild(panel);
    panel.querySelector('.side-panel-close')?.addEventListener('click', () => panel.remove());
    try {
      const { get: fetchGet } = await import('../api');
      const threads = await fetchGet<Array<{id:string;title?:string;parent_id:string;message_count?:number;updated_at?:string}>>(`/api/threads?server_id=${encodeURIComponent(sid)}&channel=${encodeURIComponent(ch)}`);
      const body = document.getElementById('threads-panel-body');
      if (!body) return;
      if (!threads.length) {
        body.innerHTML = '<div class="side-panel-empty">No threads in this channel yet.</div>';
        return;
      }
      body.innerHTML = threads.map(t => `
        <div class="thread-row" data-thread-id="${escHtml(t.id)}">
          <div class="thread-row-title">${escHtml(t.title || '(untitled thread)')}</div>
          <div class="thread-row-meta">${t.message_count ? `${t.message_count} messages` : ''} ${t.updated_at ? '· ' + escHtml(new Date(t.updated_at).toLocaleString()) : ''}</div>
        </div>`).join('');
      body.querySelectorAll<HTMLElement>('.thread-row').forEach(row => {
        row.addEventListener('click', async () => {
          const tid = row.dataset['threadId'];
          if (!tid) return;
          const { openThreadPanelById } = await import('../channels/threads');
          panel.remove();
          await openThreadPanelById(tid);
        });
      });
    } catch {
      const body = document.getElementById('threads-panel-body');
      if (body) body.textContent = 'Failed to load threads';
    }
  });

  document.getElementById('notifications-toggle')?.addEventListener('click', async () => {
    const btn = document.getElementById('notifications-toggle');
    const existing = document.getElementById('notif-popover');
    if (existing) { existing.remove(); return; }
    if (!btn) return;
    const sid = state.currentServer;
    const channelKey = `hub-people-notif:${sid ?? ''}:${String(ch)}`;
    const current = localStorage.getItem(channelKey) || 'all';
    const notifMod = await import('../components/notifications');
    const quickMuted = notifMod.isChannelMuted(String(ch));
    // Global web-push (self-hosted ntfy) enable/disable — the quick toggle The
    // Developer asked for: always reachable from the header bell, no digging in
    // Settings. Reflects + drives the same subscription as Settings → Notifications.
    const ntfyMod = await import('../components/ntfy-subscribe');
    const pushOn = ntfyMod.isNtfySubscribed();
    const pop = document.createElement('div');
    pop.id = 'notif-popover';
    pop.className = 'notif-popover';
    pop.innerHTML = `
      <div class="notif-popover-title">Push notifications</div>
      <label class="notif-popover-row">
        <input type="checkbox" id="notif-webpush" ${pushOn ? 'checked' : ''}>
        <span>Notify me on this device</span>
      </label>
      <div class="notif-popover-divider"></div>
      <div class="notif-popover-title">This channel · #${escHtml(String(ch))}</div>
      ${[['all','All Messages'],['mentions','Only @mentions'],['nothing','Nothing']].map(([k,l]) => `
        <label class="notif-popover-row">
          <input type="radio" name="notif-level" value="${k}" ${current === k ? 'checked' : ''}>
          <span>${l}</span>
        </label>`).join('')}
      <div class="notif-popover-divider"></div>
      <label class="notif-popover-row">
        <input type="checkbox" id="notif-quick-mute" ${quickMuted ? 'checked' : ''}>
        <span>Mute channel</span>
      </label>
    `;
    document.body.appendChild(pop);
    const r = btn.getBoundingClientRect();
    pop.style.position = 'fixed';
    pop.style.top = `${r.bottom + 6}px`;
    pop.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
    pop.querySelectorAll<HTMLInputElement>('input[name=notif-level]').forEach(inp => {
      inp.addEventListener('change', () => {
        localStorage.setItem(channelKey, inp.value);
      });
    });
    document.getElementById('notif-quick-mute')?.addEventListener('change', () => {
      notifMod.toggleChannelMute(String(ch));
    });
    document.getElementById('notif-webpush')?.addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      if (on) {
        // One-tap enable: request browser permission (so notifications can show)
        // then subscribe to self-hosted ntfy push (no Google/Firebase).
        if ('Notification' in window && Notification.permission === 'default') {
          notifMod.requestNotificationPermissionSync(() => { /* permission stored by the browser */ });
        }
        ntfyMod.subscribeNtfy();
        showToast('Push notifications on', 'ok');
      } else {
        ntfyMod.unsubscribeNtfy();
        showToast('Push notifications off', 'ok');
      }
    });
    const teardown = () => {
      pop.remove();
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('keydown', onKey, true);
    };
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (pop.contains(t) || (btn && btn.contains(t))) return;
      teardown();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); teardown(); }
    };
    setTimeout(() => {
      document.addEventListener('mousedown', onOutside, true);
      document.addEventListener('keydown', onKey, true);
    }, 0);
  });

  document.getElementById('inbox-toggle')?.addEventListener('click', async () => {
    const btn = document.getElementById('inbox-toggle');
    const existing = document.getElementById('ca-inbox-flyout');
    if (existing) { existing.remove(); return; }
    if (!btn) return;

    const flyout = document.createElement('div');
    flyout.id = 'ca-inbox-flyout';
    flyout.className = 'ca-inbox-flyout';
    flyout.innerHTML = `
      <div class="ca-inbox-tabs" role="tablist">
        <button class="ca-inbox-tab active" data-tab="mentions" role="tab">Mentions</button>
        <button class="ca-inbox-tab" data-tab="unreads" role="tab">Unreads</button>
        <button class="ca-inbox-tab" data-tab="all" role="tab">All</button>
      </div>
      <div class="ca-inbox-body" id="ca-inbox-body"><div class="ca-inbox-loading">Loading…</div></div>
    `;
    document.body.appendChild(flyout);
    const r = btn.getBoundingClientRect();
    flyout.style.position = 'fixed';
    flyout.style.top = `${r.bottom + 6}px`;
    flyout.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;

    type Mention = { id: string; channel: string; author_id: string; content: string; created_at: string };
    type Unread = { server_id: string; channel: string; unread_count: number; mention_count: number };
    let mentionsCache: Mention[] | null = null;
    let unreadsCache: Unread[] | null = null;

    const renderEmpty = (msg: string): string =>
      `<div class="ca-inbox-empty">${escHtml(msg)}</div>`;

    const renderMentions = (items: Mention[]): string => {
      if (!items.length) return renderEmpty("You're all caught up. Mentions will appear here.");
      return items.map(i => {
        const name = state.people.find(p => p.id === i.author_id)?.name || 'Unknown';
        const preview = i.content.length > 120 ? i.content.substring(0, 120) + '…' : i.content;
        return `<div class="ca-inbox-row" data-channel="${escHtml(i.channel)}">
          <div class="ca-inbox-row-meta">#${escHtml(i.channel)} · ${escHtml(name)} · ${escHtml(new Date(i.created_at).toLocaleString())}</div>
          <div class="ca-inbox-row-body">${escHtml(preview)}</div>
        </div>`;
      }).join('');
    };

    const renderUnreads = (items: Unread[]): string => {
      const filtered = items.filter(u => u.unread_count > 0);
      if (!filtered.length) return renderEmpty('No unread channels.');
      return filtered.map(u => `
        <div class="ca-inbox-row" data-channel="${escHtml(u.channel)}">
          <div class="ca-inbox-row-meta">#${escHtml(u.channel)}</div>
          <div class="ca-inbox-row-body">${u.unread_count} unread${u.unread_count === 1 ? '' : 's'}${u.mention_count ? ` · ${u.mention_count} mention${u.mention_count === 1 ? '' : 's'}` : ''}</div>
        </div>`).join('');
    };

    const switchTab = async (tab: 'mentions' | 'unreads' | 'all'): Promise<void> => {
      flyout.querySelectorAll<HTMLElement>('.ca-inbox-tab').forEach(t => {
        t.classList.toggle('active', t.dataset['tab'] === tab);
      });
      const bodyEl = document.getElementById('ca-inbox-body');
      if (!bodyEl) return;
      bodyEl.innerHTML = '<div class="ca-inbox-loading">Loading…</div>';
      try {
        const { get: fetchGet } = await import('../api');
        if (tab === 'mentions') {
          if (!mentionsCache) {
            mentionsCache = await fetchGet<Mention[]>(`/api/inbox/mentions?limit=20`).catch(() => []);
          }
          bodyEl.innerHTML = renderMentions(mentionsCache);
        } else if (tab === 'unreads') {
          if (!unreadsCache) {
            unreadsCache = await fetchGet<Unread[]>(`/api/unreads`).catch(() => []);
          }
          bodyEl.innerHTML = renderUnreads(unreadsCache);
        } else {
          if (!mentionsCache) {
            mentionsCache = await fetchGet<Mention[]>(`/api/inbox/mentions?limit=20`).catch(() => []);
          }
          if (!unreadsCache) {
            unreadsCache = await fetchGet<Unread[]>(`/api/unreads`).catch(() => []);
          }
          const combined = renderMentions(mentionsCache) + renderUnreads(unreadsCache);
          bodyEl.innerHTML = combined.trim() || renderEmpty('Nothing here yet.');
        }
        bodyEl.querySelectorAll<HTMLElement>('.ca-inbox-row').forEach(row => {
          row.addEventListener('click', () => {
            const chan = row.dataset['channel'];
            if (!chan) return;
            const target = state.channels.find(c => c.name === chan && c.server_id === state.currentServer);
            if (!target) { showToast(`Channel ${chan} not loaded`, 'warn'); return; }
            state.currentChannel = target.name;
            void import('../app').then(({ renderApp }) => renderApp());
            teardown();
          });
        });
      } catch {
        bodyEl.innerHTML = renderEmpty('Inbox endpoint not yet available.');
      }
    };

    flyout.querySelectorAll<HTMLElement>('.ca-inbox-tab').forEach(t => {
      t.addEventListener('click', () => {
        const tab = (t.dataset['tab'] as 'mentions' | 'unreads' | 'all') || 'mentions';
        void switchTab(tab);
      });
    });

    const teardown = (): void => {
      flyout.remove();
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('keydown', onKey, true);
    };
    const onOutside = (e: MouseEvent): void => {
      const t = e.target as Node | null;
      if (!t) return;
      if (flyout.contains(t) || btn.contains(t)) return;
      teardown();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { e.stopPropagation(); teardown(); }
    };
    setTimeout(() => {
      document.addEventListener('mousedown', onOutside, true);
      document.addEventListener('keydown', onKey, true);
    }, 0);

    void switchTab('mentions');
  });

  // Pinned messages panel — Discord parity. Click a row to jump-to-message
  // in the chat scrollback, click "Unpin" to toggle the flag back off and
  // re-render the popover.
  document.getElementById('pins-toggle')?.addEventListener('click', async () => {
    const existing = document.getElementById('pins-panel');
    if (existing) { existing.remove(); return; }

    const sid = state.currentServer;
    if (!sid) return;

    const panel = document.createElement('div');
    panel.id = 'pins-panel';
    panel.className = 'pins-panel';
    panel.innerHTML = '<div class="pins-header">Pinned Messages<button class="pins-close" id="pins-close" aria-label="Close pinned messages">&times;</button></div><div class="pins-body"><div class="pins-loading">Loading...</div></div>';
    container.appendChild(panel);

    document.getElementById('pins-close')?.addEventListener('click', () => panel.remove());

    const { get: fetchGet, post: fetchPost } = await import('../api');
    const body = panel.querySelector<HTMLElement>('.pins-body');
    if (!body) return;

    const renderPins = (pins: Array<{id:string;author_id:string;content:string;created_at:string}>): void => {
      if (pins.length === 0) {
        body.innerHTML = '<div class="pins-empty">No pinned messages in this channel.</div>';
        return;
      }
      body.innerHTML = pins.map(p => {
        const name = state.people.find(pp => pp.id === p.author_id)?.name || 'Unknown';
        const preview = p.content.length > 140 ? p.content.substring(0, 140) + '…' : p.content;
        const when = new Date(p.created_at).toLocaleString();
        return `<div class="pin-item" data-msg-id="${escHtml(p.id)}">
          <div class="pin-row-head">
            <span class="pin-author">${escHtml(name)}</span>
            <span class="pin-time">${escHtml(when)}</span>
          </div>
          <div class="pin-content">${escHtml(preview) || '<em class="pin-empty">(attachment)</em>'}</div>
          <div class="pin-actions">
            <button type="button" class="pin-jump" data-msg-id="${escHtml(p.id)}">Jump</button>
            <button type="button" class="pin-unpin" data-msg-id="${escHtml(p.id)}">Unpin</button>
          </div>
        </div>`;
      }).join('');

      body.querySelectorAll<HTMLButtonElement>('.pin-jump').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset['msgId'] || '';
          jumpToMessage(id);
        });
      });
      body.querySelectorAll<HTMLButtonElement>('.pin-unpin').forEach(btn => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset['msgId'] || '';
          btn.disabled = true;
          try {
            await fetchPost('/api/messages/pin', { id, pinned: false });
            const fresh = await fetchGet<Array<{id:string;author_id:string;content:string;created_at:string}>>(`/api/messages/pinned?server_id=${encodeURIComponent(sid)}&channel=${encodeURIComponent(ch)}`);
            renderPins(fresh);
            showToast('Unpinned', 'ok');
          } catch {
            btn.disabled = false;
            showToast('Failed to unpin', 'err');
          }
        });
      });
    };

    try {
      const pins = await fetchGet<Array<{id:string;author_id:string;content:string;created_at:string}>>(`/api/messages/pinned?server_id=${encodeURIComponent(sid)}&channel=${encodeURIComponent(ch)}`);
      renderPins(pins);
    } catch { body.textContent = 'Failed to load pins'; }
  });

  // Lock badge → jump straight to the Encryption tab in channel settings.
  document.getElementById('ca-enc-badge')?.addEventListener('click', async () => {
    const btn = document.getElementById('ca-enc-badge') as HTMLButtonElement | null;
    if (!btn) return;
    const cid = btn.dataset['channelId'] || '';
    const cname = btn.dataset['channelName'] || '';
    if (!cid) return;
    const { openChannelSettings } = await import('../components/channel-settings');
    openChannelSettings(cid, cname, 'encryption');
  });

  // Channel topic click → open the channel-settings General tab so edits
  // actually persist. prompt() + no save was a cul-de-sac: on phones the
  // native dialog covers the whole screen and the value went nowhere.
  document.getElementById('ca-topic')?.addEventListener('click', async () => {
    const topicEl = document.getElementById('ca-topic') as HTMLElement | null;
    if (!topicEl || !state.currentServer) return;
    const cid = topicEl.dataset['channelId'] || '';
    const cname = topicEl.dataset['channelName'] || topicEl.textContent || '';
    if (!cid) return;
    const { openChannelSettings } = await import('../components/channel-settings');
    openChannelSettings(cid, cname, 'general');
  });

  // Toggle member list. On mobile (<768px) the panel overlays as a right-hand
  // drawer via .mobile-open; on desktop it's an inline column via .open. The
  // toggle button gets `.active` when the panel is visible so Mitchell can see
  // a second tap will close it. Persist per-server in localStorage.
  const membersBtn = document.getElementById('members-toggle');
  const ml = document.getElementById('member-list');
  const sid = state.currentServer;
  const storageKey = sid ? `hub-people-members-open:${sid}` : 'hub-people-members-open';
  if (ml && window.innerWidth < 768) {
    // On mobile: always reset the member-list drawer when switching channels
    // so the scrim and overlay don't stay stuck from a previous channel view.
    ml.classList.remove('mobile-open');
    document.getElementById('sidebar-scrim')?.classList.remove('open');
    membersBtn?.classList.remove('active');
  } else if (ml) {
    const wantOpen = localStorage.getItem(storageKey) === 'true';
    ml.classList.toggle('open', wantOpen);
    membersBtn?.classList.toggle('active', wantOpen);
  }
  membersBtn?.addEventListener('click', () => {
    const panel = document.getElementById('member-list');
    if (!panel) return;
    const onMobile = window.innerWidth < 768;
    const cls = onMobile ? 'mobile-open' : 'open';
    const nowOpen = !panel.classList.contains(cls);
    panel.classList.toggle(cls, nowOpen);
    membersBtn.classList.toggle('active', nowOpen);
    if (onMobile) {
      // Keep the shared scrim in sync so tapping outside the drawer closes it.
      document.getElementById('sidebar-scrim')?.classList.toggle('open', nowOpen);
    } else {
      localStorage.setItem(storageKey, String(nowOpen));
    }
  });

  // Search
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  const searchInput = document.getElementById('ca-search') as HTMLInputElement | null;
  searchInput?.addEventListener('input', () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(async () => {
      const q = searchInput.value.trim();
      if (q.length < 2) return;
      try {
        const { get: fetchGet } = await import('../api');
        const results = await fetchGet<Array<{kind:string;id:string;title:string;preview:string;context:string}>>(`/api/search?q=${encodeURIComponent(q)}&limit=10`);
        let dropdown = document.getElementById('search-results');
        if (!dropdown) {
          dropdown = document.createElement('div');
          dropdown.id = 'search-results';
          dropdown.className = 'ca-search-results';
          document.getElementById('ca-search-wrap')?.appendChild(dropdown);
          wireSearchResultClicks(dropdown, searchInput);
        }
        if (results.length === 0) {
          dropdown.innerHTML = '<div class="search-empty">No results</div>';
        } else {
          const kindIcons: Record<string, string> = { message: '#', person: '@', dm: '&#9993;' };
          dropdown.innerHTML = results.map(r => {
            const icon = kindIcons[r.kind] || '?';
            const preview = r.preview.length > 80 ? r.preview.substring(0, 80) + '...' : r.preview;
            return `<div class="search-item" data-kind="${escHtml(r.kind)}" data-id="${escHtml(r.id)}" data-title="${escHtml(r.title)}" data-context="${escHtml(r.context)}">
              <span class="search-kind-icon">${icon}</span>
              <div class="search-item-body">
                <div class="search-item-title">${escHtml(r.title)}</div>
                <div class="search-item-preview">${escHtml(preview)}</div>
                ${r.context ? `<div class="search-item-context">${escHtml(r.context)}</div>` : ''}
              </div>
            </div>`;
          }).join('');
        }
      } catch { /* silent */ }
    }, 300);
  });
  searchInput?.addEventListener('blur', () => {
    setTimeout(() => {
      document.getElementById('search-results')?.remove();
    }, 200);
  });

  // Voice channels get the LiveKit panel; everything else routes to a
  // management channel view or the data-driven chat view.
  if (isVoice) {
    if (voiceCh) {
      renderVoicePanel(body, voiceCh);
    } else {
      body.innerHTML = `<div class="ca-empty-voice" style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:var(--fg-muted);gap:8px"><div style="font-size:18px">Voice channel unavailable</div></div>`;
    }
    return;
  }

  // Polymorphic channel_kind registry. When channel_kind is set on the row
  // (migration 020+), dispatch by kind; otherwise fall through to the legacy
  // name-based switch. Keep the registry small and declarative — adding a new
  // kind is one line here + one renderer file.
  const KIND_RENDERERS: Record<string, (el: HTMLElement) => void> = {
    text: (el) => renderChat(el, ch),
    voice: (el) => {
      if (voiceCh) renderVoicePanel(el, voiceCh);
      else el.innerHTML = '<div style="padding:20px;color:var(--fg-muted)">Voice channel unavailable.</div>';
    },
    email: (el) => { void renderEmailInbox(el); },
    sms: (el) => { void renderSms(el); },
    cell_status: (el) => { void renderCellStatus(el); },
    call_log: (el) => { void renderCalls(el); },
    // notes_137: dedicated "137 notes" channel — read-only timeline of dictation
    // notes captured by the 137 callback pipeline (transcript + inline audio).
    // The note-taking config lives in Calls > Automation, not here.
    notes_137: (el) => { void renderNotes137(el); },
    // ai_assistant: same UX as a regular text channel — server-side post-INSERT
    // hook in handlers/ai_chat.rs auto-replies as bot-ai-chat. Frontend doesn't
    // need a special renderer; mapping to renderChat removes the "Unknown
    // channel kind" banner.
    ai_assistant: (el) => renderChat(el, ch),
    // ai_channel: same pattern — Mitchell Space's "assistant" channel.
    ai_channel: (el) => renderChat(el, ch),
    // peer_chat: "the peer" — Anthropic-style NL chat, dev9 brief 2026-04-26.
    peer_chat: (el) => renderPeerChat(el, ch),
    // watcher_log: hub-watcher's #watcher channel — GitHub-Actions-runs-style
    // timeline of cycle cards with status pills, metadata grids, and filters.
    // Mitchell ask 2026-04-27. Polls /api/messages every 10s.
    watcher_log: (el) => renderWatcherFeed(el),
    // dev_dashboard: #devs channel — Vercel-style cards for hub-dev*/watcher.
    // Polls /api/devs/status every 5s; snapshot is written by hub-watcher.
    dev_dashboard: (el) => renderDevDashboard(el),
    development: (el) => { void renderDevelopment(el); },
    board: (el) => { void renderBoard(el, channelRow?.category || ''); },
    system_health: (el) => { void renderSystemHealth(el); },
    ai_tasks: (el) => { void renderAiTasks(el); },
    docs: (el) => { void renderDocs(el); },
    // blog: Marketing > "blog" channel — review DRAFT posts for the public
    // The Hub Ecosystems blog. Approve or leave notes. Renderer in blog.ts.
    blog: (el) => { void renderBlog(el); },
    ai_questions: (el) => { void renderAiQuestions(el, String(ch)); },
  };
  if (channelRow?.channel_kind) {
    const kind = channelRow.channel_kind;
    const renderer = KIND_RENDERERS[kind];
    if (renderer) {
      if (kind !== 'voice') body.classList.add('ca-body-chat');
      renderer(body);
      return;
    }
    // Unknown kind — silently fall back to text renderer. This handles the
    // case where a new channel_kind is added server-side but the user's
    // browser still has a cached JS bundle without that renderer. No error
    // banner — just works as a chat channel until the next SW update.
    if (kind !== 'text') {
      body.classList.add('ca-body-chat');
      renderChat(body, ch);
      return;
    }
  }

  switch (ch) {
    case 'agreements': renderAgreements(body); break;
    case 'approvals': renderApprovals(body); break;
    case 'contractor': renderContractor(body); break;
    case 'directory': renderDirectory(body); break;
    case 'roles': renderRoles(body); break;
    case 'a-b-testing': renderABTesting(body); break;
    case 'rules': renderRules(body); break;
    case 'logs': renderLogs(body); break;
    case 'settings': renderSettings(body); break;
    case 'devs-launch':
      if (state.flags['dev_session_console'] !== false) {
        renderDevsLaunch(body);
      } else {
        body.innerHTML = '<div style="padding:20px;color:var(--fg-muted)">Dev session console is currently disabled.</div>';
      }
      break;
    case 'email-inbox': void renderEmailInbox(body); break;
    default: {
      // Chat views: prevent ca-body from scrolling so compose bar stays
      // pinned on mobile (nested-scroll fix).
      body.classList.add('ca-body-chat');
      // Route cc_chat channels to the dedicated CC renderer.
      const chRow = state.channels.find(c => c.name === String(ch) && c.server_id === state.currentServer);
      if (chRow?.channel_type === 'cc_chat' && state.flags['cc_chat_channel'] !== false) {
        renderCcChat(body, ch);
      } else {
        renderChat(body, ch);
      }
      break;
    }
  }
}

/** Scroll the chat scrollback to the given message id and flash it. Used by
 *  the pinned-list popover; safe no-op if the message isn't currently in the
 *  rendered window (it lives off-screen until the user paginates to it). */
function jumpToMessage(id: string): void {
  if (!id) return;
  const el = document.querySelector(`#chat-messages [data-id="${CSS.escape(id)}"]`) as HTMLElement | null;
  if (!el) {
    showToast('Message is older than the loaded window — scroll up to find it.', 'warn');
    return;
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('msg-jump-flash');
  setTimeout(() => el.classList.remove('msg-jump-flash'), 1500);
}

function escHtml(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function wireSearchResultClicks(dropdown: HTMLElement, searchInput: HTMLInputElement | null): void {
  dropdown.addEventListener('click', (e) => {
    const item = (e.target as HTMLElement).closest<HTMLElement>('.search-item');
    if (!item) return;
    const kind = item.dataset['kind'];
    const id = item.dataset['id'] || '';
    const title = item.dataset['title'] || '';
    const context = item.dataset['context'] || '';
    const close = () => {
      if (searchInput) searchInput.value = '';
      dropdown.remove();
    };
    if (kind === 'person' && id) {
      openProfileCard(id, item);
      close();
      return;
    }
    if (kind === 'message') {
      const chanName = context.startsWith('#') ? context.slice(1) : context;
      if (!chanName) { showToast('Cannot locate channel', 'warn'); close(); return; }
      const target = state.channels.find(c => c.name === chanName && c.server_id === state.currentServer);
      if (!target) {
        const crossServer = state.channels.find(c => c.name === chanName);
        showToast(crossServer ? `${context} is in another server — open it first` : `Channel ${context} not loaded`, 'warn');
        close();
        return;
      }
      state.currentChannel = target.name;
      void import('../app').then(({ renderApp }) => renderApp());
      close();
      return;
    }
    if (kind === 'dm') {
      const partner = state.people.find(p => p.name === title);
      if (!partner) { showToast('DM partner not found', 'warn'); close(); return; }
      window.dispatchEvent(new CustomEvent('hub-open-dm', { detail: { personId: partner.id } }));
      close();
      return;
    }
    close();
  });
}

/**
 * Empty + hide the right-side server member-list aside. Called whenever we
 * leave a server channel (DM home, DM conversation) so the roster from the
 * last server doesn't render alongside the DM's Conversation panel.
 */
function clearServerMemberList(): void {
  const ml = document.getElementById('member-list');
  if (!ml) return;
  ml.innerHTML = '';
  ml.classList.remove('open');
  ml.classList.remove('mobile-open');
}

/** Show skeleton placeholders while data is loading */
export function renderContentSkeleton(container: HTMLElement): void {
  container.innerHTML = `
    <div class="ca-header">
      <span class="ca-header-icon">${iconHash()}</span>
      <span class="ca-header-title"><div class="skeleton skeleton-line" style="width:100px;display:inline-block"></div></span>
    </div>
  `;
  const body = document.createElement('div');
  body.className = 'ca-body';
  body.innerHTML = skeletonStats(4) + skeletonCard(4);
  container.appendChild(body);
}

